import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  TextField
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  PhoneIphone as PhoneIcon,
  Menu as MenuIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import { DisplaySettings as DisplaySettingsType } from '../../../types/storeSettings';
import { useStoreSettings } from '../../../contexts/StoreSettingsContext';

interface MobileThemeEditorProps {
  settings: DisplaySettingsType;
  onSave: (loading: boolean) => void;
  onError: (error: string | null) => void;
  onSuccess: (success: boolean) => void;
}

const defaultBottomBarItems = [
  { id: 'home', label: 'الرئيسية', icon: 'home', link: '/', enabled: true },
  { id: 'categories', label: 'الأقسام', icon: 'category', link: '/categories', enabled: true },
  { id: 'cart', label: 'السلة', icon: 'shopping_cart', link: '/cart', enabled: true },
  { id: 'wishlist', label: 'المفضلة', icon: 'favorite', link: '/favorites', enabled: true },
  { id: 'profile', label: 'حسابي', icon: 'person', link: '/account', enabled: true }
];

const defaultMainMenuItems = [
  { id: 'home', title: 'الرئيسية', icon: 'home', path: '/', enabled: true, color: '#0d51d6' },
  { id: 'offers', title: 'العروض الخاصة', icon: 'local_offer', path: '/offers', enabled: true, color: '#f44336', badge: 'جديد' },
  { id: 'bestsellers', title: 'الأكثر مبيعاً', icon: 'trending_up', path: '/bestsellers', enabled: true, color: '#4caf50' },
  { id: 'featured', title: 'المنتجات المميزة', icon: 'star', path: '/featured', enabled: true, color: '#ff9800' }
];

const defaultBottomMenuItems = [
  { id: 'about', title: 'حول المتجر', icon: 'info', path: '/about', enabled: true },
  { id: 'contact', title: 'اتصل بنا', icon: 'phone', path: '/contact', enabled: true },
  { id: 'profile', title: 'حسابي', icon: 'person', path: '/profile', enabled: true }
];

const iconOptions = [
  { value: 'home', label: 'Home (الرئيسية)' },
  { value: 'category', label: 'Category (الأقسام)' },
  { value: 'shopping_cart', label: 'Shopping Cart (السلة)' },
  { value: 'favorite', label: 'Favorite (المفضلة)' },
  { value: 'person', label: 'Person (حسابي)' },
  { value: 'store', label: 'Store (المتجر)' },
  { value: 'search', label: 'Search (البحث)' },
  { value: 'notifications', label: 'Notifications (الإشعارات)' },
  { value: 'settings', label: 'Settings (الإعدادات)' },
  { value: 'local_offer', label: 'Offer (العروض)' },
  { value: 'trending_up', label: 'Trending (الأكثر مبيعاً)' },
  { value: 'star', label: 'Star (المميزة)' },
  { value: 'info', label: 'Info (حول المتجر)' },
  { value: 'phone', label: 'Phone (اتصل بنا)' }
];

const MobileThemeEditor: React.FC<MobileThemeEditorProps> = ({
  settings,
  onSave,
  onError,
  onSuccess
}) => {
  const { updateSection } = useStoreSettings();
  const [formData, setFormData] = useState<DisplaySettingsType>(settings);
  const [editing, setEditing] = useState(false);

  // تحديث البيانات المحلية عند تغيير الإعدادات الخارجية
  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  const handleChange = (name: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleItemToggle = (id: string, enabled: boolean) => {
    const currentItems = formData.mobileBottomBarItems || defaultBottomBarItems;
    const updatedItems = currentItems.map(item =>
      item.id === id ? { ...item, enabled } : item
    );
    handleChange('mobileBottomBarItems', updatedItems);
  };

  const handleItemLabelChange = (id: string, label: string) => {
    const currentItems = formData.mobileBottomBarItems || defaultBottomBarItems;
    const updatedItems = currentItems.map(item =>
      item.id === id ? { ...item, label } : item
    );
    handleChange('mobileBottomBarItems', updatedItems);
  };

  const handleItemIconChange = (id: string, icon: string) => {
    const currentItems = formData.mobileBottomBarItems || defaultBottomBarItems;
    const updatedItems = currentItems.map(item =>
      item.id === id ? { ...item, icon } : item
    );
    handleChange('mobileBottomBarItems', updatedItems);
  };

  const handleMoveItem = (index: number, direction: 'up' | 'down') => {
    const currentItems = [...(formData.mobileBottomBarItems || defaultBottomBarItems)];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= currentItems.length) return;

    // تبديل الأماكن
    const temp = currentItems[index];
    currentItems[index] = currentItems[targetIndex];
    currentItems[targetIndex] = temp;

    handleChange('mobileBottomBarItems', currentItems);
  };

  // Main Menu Items Handlers
  const handleMainMenuItemToggle = (id: string, enabled: boolean) => {
    const currentItems = formData.mobileMainMenuItems || defaultMainMenuItems;
    const updatedItems = currentItems.map(item =>
      item.id === id ? { ...item, enabled } : item
    );
    handleChange('mobileMainMenuItems', updatedItems);
  };

  const handleMainMenuItemChange = (id: string, field: string, value: any) => {
    const currentItems = formData.mobileMainMenuItems || defaultMainMenuItems;
    const updatedItems = currentItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    );
    handleChange('mobileMainMenuItems', updatedItems);
  };

  const handleMoveMainMenuItem = (index: number, direction: 'up' | 'down') => {
    const currentItems = [...(formData.mobileMainMenuItems || defaultMainMenuItems)];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= currentItems.length) return;

    const temp = currentItems[index];
    currentItems[index] = currentItems[targetIndex];
    currentItems[targetIndex] = temp;

    handleChange('mobileMainMenuItems', currentItems);
  };

  const handleAddMainMenuItem = () => {
    const currentItems = formData.mobileMainMenuItems || defaultMainMenuItems;
    const newItem = {
      id: `custom_${Date.now()}`,
      title: 'عنصر جديد',
      icon: 'home',
      path: '/',
      enabled: true,
      color: '#0d51d6'
    };
    handleChange('mobileMainMenuItems', [...currentItems, newItem]);
  };

  const handleRemoveMainMenuItem = (id: string) => {
    const currentItems = formData.mobileMainMenuItems || defaultMainMenuItems;
    const updatedItems = currentItems.filter(item => item.id !== id);
    handleChange('mobileMainMenuItems', updatedItems);
  };

  // Bottom Menu Items Handlers
  const handleBottomMenuItemToggle = (id: string, enabled: boolean) => {
    const currentItems = formData.mobileBottomMenuItems || defaultBottomMenuItems;
    const updatedItems = currentItems.map(item =>
      item.id === id ? { ...item, enabled } : item
    );
    handleChange('mobileBottomMenuItems', updatedItems);
  };

  const handleBottomMenuItemChange = (id: string, field: string, value: any) => {
    const currentItems = formData.mobileBottomMenuItems || defaultBottomMenuItems;
    const updatedItems = currentItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    );
    handleChange('mobileBottomMenuItems', updatedItems);
  };

  const handleMoveBottomMenuItem = (index: number, direction: 'up' | 'down') => {
    const currentItems = [...(formData.mobileBottomMenuItems || defaultBottomMenuItems)];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= currentItems.length) return;

    const temp = currentItems[index];
    currentItems[index] = currentItems[targetIndex];
    currentItems[targetIndex] = temp;

    handleChange('mobileBottomMenuItems', currentItems);
  };

  const handleAddBottomMenuItem = () => {
    const currentItems = formData.mobileBottomMenuItems || defaultBottomMenuItems;
    const newItem = {
      id: `custom_${Date.now()}`,
      title: 'عنصر جديد',
      icon: 'info',
      path: '/about',
      enabled: true
    };
    handleChange('mobileBottomMenuItems', [...currentItems, newItem]);
  };

  const handleRemoveBottomMenuItem = (id: string) => {
    const currentItems = formData.mobileBottomMenuItems || defaultBottomMenuItems;
    const updatedItems = currentItems.filter(item => item.id !== id);
    handleChange('mobileBottomMenuItems', updatedItems);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      onSave(true);
      onError(null);

      const dataToSave = {
        ...formData,
        mobileBottomBarItems: formData.mobileBottomBarItems || defaultBottomBarItems,
        mobileMainMenuItems: formData.mobileMainMenuItems || defaultMainMenuItems,
        mobileBottomMenuItems: formData.mobileBottomMenuItems || defaultBottomMenuItems
      };

      await updateSection('display', dataToSave);
      onSuccess(true);
      setEditing(false);
    } catch (error) {
      console.error('Error saving mobile settings:', error);
      onError('حدث خطأ أثناء حفظ إعدادات الجوال');
    } finally {
      onSave(false);
    }
  };

  const currentItems = formData.mobileBottomBarItems || defaultBottomBarItems;

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2" fontWeight="bold">
          تخصيص الهاتف المحمول (Mobile Editor)
        </Typography>

        {!editing ? (
          <Button
            startIcon={<EditIcon />}
            onClick={() => setEditing(true)}
            variant="outlined"
          >
            تعديل إعدادات الموبايل
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setFormData(settings);
                setEditing(false);
              }}
            >
              إلغاء
            </Button>
            <Button
              type="submit"
              startIcon={<SaveIcon />}
              variant="contained"
              color="primary"
            >
              حفظ التغييرات
            </Button>
          </Box>
        )}
      </Box>

      {/* زر لتفعيل التحرير مباشرة للمساعدة */}
      {!editing && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={() => setEditing(true)}
          >
            انقر هنا لتعديل وحفظ شريط التنقل للموبايل
          </Button>
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        {/* إعدادات الشريط العام */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, height: '100%' }} elevation={1}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon color="primary" /> إعدادات شريط التنقل السفلي للموبايل
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.mobileBottomBarEnabled !== false}
                    onChange={(e) => handleChange('mobileBottomBarEnabled', e.target.checked)}
                    disabled={!editing}
                  />
                }
                label="تفعيل شريط التنقل السفلي بالكامل"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.mobileBottomBarShowLabels !== false}
                    onChange={(e) => handleChange('mobileBottomBarShowLabels', e.target.checked)}
                    disabled={!editing || formData.mobileBottomBarEnabled === false}
                  />
                }
                label="إظهار النصوص أسفل الأيقونات"
              />

              <FormControl fullWidth>
                <InputLabel id="mobile-bar-style-select-label">نمط الشريط (Bar Style)</InputLabel>
                <Select
                  labelId="mobile-bar-style-select-label"
                  value={formData.mobileBottomBarStyle || 'classic'}
                  disabled={!editing || formData.mobileBottomBarEnabled === false}
                  onChange={(e) => handleChange('mobileBottomBarStyle', e.target.value)}
                  label="نمط الشريط (Bar Style)"
                >
                  <MenuItem value="classic">كلاسيكي كامل العرض (Classic)</MenuItem>
                  <MenuItem value="floating">عائم مع زوايا دائرية (Floating)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>
        </Grid>

        {/* عناصر الشريط */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, height: '100%' }} elevation={1}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <MenuIcon color="primary" /> تخصيص وترتيب عناصر الشريط السفلي
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              تحكم في ظهور وترتيب ونصوص عناصر شريط التنقل السفلي للهاتف المحمول.
            </Typography>

            <List sx={{ width: '100%', bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
              {currentItems.map((item, index) => (
                <ListItem
                  key={item.id}
                  sx={{
                    borderBottom: index < currentItems.length - 1 ? '1px solid #eaeaea' : 'none',
                    bgcolor: item.enabled ? 'transparent' : '#f9f9f9',
                    opacity: item.enabled && formData.mobileBottomBarEnabled !== false ? 1 : 0.6,
                    flexWrap: 'wrap',
                    py: 2
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <DragIcon color="action" />
                  </ListItemIcon>
                  
                  {/* بيانات التعديل للمكون */}
                  <Grid container spacing={1} sx={{ flexGrow: 1, maxWidth: 'calc(100% - 130px)' }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="العنوان المرئي"
                        value={item.label}
                        disabled={!editing || !item.enabled || formData.mobileBottomBarEnabled === false}
                        onChange={(e) => handleItemLabelChange(item.id, e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>الأيقونة</InputLabel>
                        <Select
                          value={item.icon}
                          disabled={!editing || !item.enabled || formData.mobileBottomBarEnabled === false}
                          onChange={(e) => handleItemIconChange(item.id, e.target.value as string)}
                          label="الأيقونة"
                        >
                          {iconOptions.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 0.5, position: 'relative', top: 'auto', right: 'auto', transform: 'none' }}>
                    {/* التفعيل */}
                    <Switch
                      edge="end"
                      checked={item.enabled}
                      disabled={!editing || formData.mobileBottomBarEnabled === false}
                      onChange={(e) => handleItemToggle(item.id, e.target.checked)}
                    />
                    
                    {/* الترتيب للأعلى */}
                    <IconButton
                      size="small"
                      disabled={!editing || index === 0 || formData.mobileBottomBarEnabled === false}
                      onClick={() => handleMoveItem(index, 'up')}
                    >
                      <UpIcon fontSize="small" />
                    </IconButton>

                    {/* الترتيب للأسفل */}
                    <IconButton
                      size="small"
                      disabled={!editing || index === currentItems.length - 1 || formData.mobileBottomBarEnabled === false}
                      onClick={() => handleMoveItem(index, 'down')}
                    >
                      <DownIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />
      
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        تخصيص القائمة الجانبية للهاتف (Mobile Sidebar Customization)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
        تحكم بشكل كامل في العناصر التي تظهر في القائمة الجانبية للموبايل، ترتيبها، أيقوناتها، ألوانها، وتفعيلها.
      </Typography>

      <Grid container spacing={3}>
        {/* عناصر القائمة الجانبية الرئيسية */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MenuIcon color="primary" /> القائمة الرئيسية (العلوية)
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                disabled={!editing}
                onClick={handleAddMainMenuItem}
              >
                إضافة عنصر جديد
              </Button>
            </Box>
            
            <List sx={{ width: '100%', bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
              {(formData.mobileMainMenuItems || defaultMainMenuItems).map((item, index, arr) => (
                <ListItem
                  key={item.id}
                  sx={{
                    borderBottom: index < arr.length - 1 ? '1px solid #eaeaea' : 'none',
                    bgcolor: item.enabled ? 'transparent' : '#f9f9f9',
                    opacity: item.enabled ? 1 : 0.6,
                    flexWrap: 'wrap',
                    py: 2
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <DragIcon color="action" />
                  </ListItemIcon>
                  
                  <Grid container spacing={2} sx={{ flexGrow: 1, maxWidth: { xs: '100%', sm: 'calc(100% - 220px)' } }}>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        size="small"
                        fullWidth
                        label="العنوان"
                        value={item.title}
                        disabled={!editing || !item.enabled}
                        onChange={(e) => handleMainMenuItemChange(item.id, 'title', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2.5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>الأيقونة</InputLabel>
                        <Select
                          value={item.icon}
                          disabled={!editing || !item.enabled}
                          onChange={(e) => handleMainMenuItemChange(item.id, 'icon', e.target.value)}
                          label="الأيقونة"
                        >
                          {iconOptions.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={2.5}>
                      <TextField
                        size="small"
                        fullWidth
                        label="المسار/الرابط"
                        value={item.path}
                        disabled={!editing || !item.enabled}
                        onChange={(e) => handleMainMenuItemChange(item.id, 'path', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        size="small"
                        fullWidth
                        label="اللون"
                        value={item.color || ''}
                        disabled={!editing || !item.enabled}
                        placeholder="#0d51d6"
                        onChange={(e) => handleMainMenuItemChange(item.id, 'color', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        size="small"
                        fullWidth
                        label="شارة (Badge)"
                        value={item.badge || ''}
                        disabled={!editing || !item.enabled}
                        placeholder="جديد"
                        onChange={(e) => handleMainMenuItemChange(item.id, 'badge', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                  
                  <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 0.5, position: 'relative', top: 'auto', right: 'auto', transform: 'none', mt: { xs: 1, sm: 0 }, ml: { xs: 5, sm: 0 } }}>
                    <Switch
                      edge="end"
                      checked={item.enabled}
                      disabled={!editing}
                      onChange={(e) => handleMainMenuItemToggle(item.id, e.target.checked)}
                    />
                    
                    <IconButton
                      size="small"
                      disabled={!editing || index === 0}
                      onClick={() => handleMoveMainMenuItem(index, 'up')}
                    >
                      <UpIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      disabled={!editing || index === arr.length - 1}
                      onClick={() => handleMoveMainMenuItem(index, 'down')}
                    >
                      <DownIcon fontSize="small" />
                    </IconButton>

                    <Button
                      size="small"
                      color="error"
                      disabled={!editing}
                      onClick={() => handleRemoveMainMenuItem(item.id)}
                    >
                      حذف
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* عناصر القائمة الجانبية السفلية */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MenuIcon color="primary" /> القائمة الفرعية (السفلية)
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                disabled={!editing}
                onClick={handleAddBottomMenuItem}
              >
                إضافة عنصر جديد
              </Button>
            </Box>
            
            <List sx={{ width: '100%', bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
              {(formData.mobileBottomMenuItems || defaultBottomMenuItems).map((item, index, arr) => (
                <ListItem
                  key={item.id}
                  sx={{
                    borderBottom: index < arr.length - 1 ? '1px solid #eaeaea' : 'none',
                    bgcolor: item.enabled ? 'transparent' : '#f9f9f9',
                    opacity: item.enabled ? 1 : 0.6,
                    flexWrap: 'wrap',
                    py: 2
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <DragIcon color="action" />
                  </ListItemIcon>
                  
                  <Grid container spacing={2} sx={{ flexGrow: 1, maxWidth: { xs: '100%', sm: 'calc(100% - 220px)' } }}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        label="العنوان"
                        value={item.title}
                        disabled={!editing || !item.enabled}
                        onChange={(e) => handleBottomMenuItemChange(item.id, 'title', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>الأيقونة</InputLabel>
                        <Select
                          value={item.icon}
                          disabled={!editing || !item.enabled}
                          onChange={(e) => handleBottomMenuItemChange(item.id, 'icon', e.target.value)}
                          label="الأيقونة"
                        >
                          {iconOptions.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        label="المسار/الرابط"
                        value={item.path}
                        disabled={!editing || !item.enabled}
                        onChange={(e) => handleBottomMenuItemChange(item.id, 'path', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                  
                  <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 0.5, position: 'relative', top: 'auto', right: 'auto', transform: 'none', mt: { xs: 1, sm: 0 }, ml: { xs: 5, sm: 0 } }}>
                    <Switch
                      edge="end"
                      checked={item.enabled}
                      disabled={!editing}
                      onChange={(e) => handleBottomMenuItemToggle(item.id, e.target.checked)}
                    />
                    
                    <IconButton
                      size="small"
                      disabled={!editing || index === 0}
                      onClick={() => handleMoveBottomMenuItem(index, 'up')}
                    >
                      <UpIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      disabled={!editing || index === arr.length - 1}
                      onClick={() => handleMoveBottomMenuItem(index, 'down')}
                    >
                      <DownIcon fontSize="small" />
                    </IconButton>

                    <Button
                      size="small"
                      color="error"
                      disabled={!editing}
                      onClick={() => handleRemoveBottomMenuItem(item.id)}
                    >
                      حذف
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {editing && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setFormData(settings);
              setEditing(false);
            }}
          >
            إلغاء التغييرات
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            حفظ إعدادات الجوال
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default MobileThemeEditor;
